import React from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, ArrowLeft, Home, Calendar } from 'lucide-react';
import { Event } from '../types';

interface NavigationBreadcrumbsProps {
  event?: Event | null;
  effectiveEventId?: string | null;
}

export default function NavigationBreadcrumbs({ event, effectiveEventId }: NavigationBreadcrumbsProps) {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Do not render breadcrumbs on root events list or public/auth pages
  const pathname = location.pathname;
  if (
    pathname === '/' || 
    pathname === '/login' || 
    pathname.startsWith('/rsvp') || 
    pathname.startsWith('/c/') || 
    pathname.startsWith('/t/') || 
    pathname.startsWith('/ticket') || 
    pathname.startsWith('/submit-')
  ) {
    return null;
  }

  const eventId = params.eventId || (pathname.includes('/events/') ? pathname.split('/events/')[1]?.split('/')[0] : null);
  const fromSource = searchParams.get('from');
  const accountManager = searchParams.get('accountManager');
  const clientManager = searchParams.get('clientManager');

  // Derive Current Page Details
  let trail: { label: string; to?: string }[] = [{ label: 'Acasă', to: '/' }];
  let backAction: { label: string; to: string } | null = null;

  if (eventId) {
    const eventName = event?.name || event?.title || 'Eveniment';
    const eventDashboardUrl = `/events/${eventId}/dashboard`;

    trail.push({
      label: eventName,
      to: eventDashboardUrl
    });

    if (pathname.includes('/dashboard')) {
      trail.push({ label: 'Dashboard' });
      backAction = { label: 'Toate Evenimentele', to: '/' };
    } else if (pathname.includes('/mktm')) {
      trail.push({ label: 'Organizare MKTM' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/distributors')) {
      trail.push({ label: 'Distribuitori' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/partner')) {
      trail.push({ label: 'Portal Parteneri' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/guests/staff')) {
      trail.push({ label: 'Staff MKTM' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/guests/resellers')) {
      const label = accountManager ? `Reselleri (${accountManager})` : 'Invitați Reselleri';
      trail.push({ label });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/guests/end-users')) {
      const label = clientManager ? `End Useri (${clientManager})` : 'Invitați End Useri (Lenovo)';
      trail.push({ label });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/guests/managers')) {
      trail.push({ label: 'Invitați pe Manageri' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/guests/partner-end-users')) {
      trail.push({ label: 'Invitați End Useri (Reselleri)' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/validations/phones')) {
      trail.push({ label: 'Validări Înscrieri', to: `/events/${eventId}/validations` });
      trail.push({ label: 'Validări Telefoane' });
      if (fromSource === 'clients') {
        backAction = { label: 'Baza Date Clienți', to: '/clients' };
      } else {
        backAction = { label: 'Dashboard', to: eventDashboardUrl };
      }
    } else if (pathname.includes('/validations')) {
      trail.push({ label: 'Validări Înscrieri' });
      if (fromSource === 'clients') {
        backAction = { label: 'Baza Date Clienți', to: '/clients' };
      } else {
        backAction = { label: 'Dashboard', to: eventDashboardUrl };
      }
    } else if (pathname.includes('/scanner')) {
      trail.push({ label: 'Check-in Scanner' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/invitations')) {
      trail.push({ label: 'Materiale Marketing' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/sms')) {
      trail.push({ label: 'Campanii SMS' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/help') || pathname.includes('/manual')) {
      trail.push({ label: 'Manual de Utilizare' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else if (pathname.includes('/settings')) {
      trail.push({ label: 'Integrări API' });
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    } else {
      backAction = { label: 'Dashboard', to: eventDashboardUrl };
    }
  } else {
    // Global Administration Pages
    trail.push({ label: 'Administrare' });
    const targetEvId = effectiveEventId || localStorage.getItem('lenovo_last_event_id');

    if (pathname === '/clients') {
      trail.push({ label: 'Baza Date Clienți' });
    } else if (pathname === '/team') {
      trail.push({ label: 'Echipa Lenovo' });
    } else if (pathname === '/mktm-team' || pathname === '/mktm') {
      trail.push({ label: 'Echipa MKTM' });
    } else if (pathname === '/settings') {
      trail.push({ label: 'Integrări' });
    } else if (pathname === '/help' || pathname === '/manual') {
      trail.push({ label: 'Manual Utilizare' });
    }

    if (targetEvId) {
      backAction = { label: 'Eveniment Activ', to: `/events/${targetEvId}/dashboard` };
    } else {
      backAction = { label: 'Lista Evenimente', to: '/' };
    }
  }

  return (
    <nav 
      aria-label="Breadcrumb & Context Navigation" 
      className="bg-white border-b border-slate-200/90 shadow-2xs"
    >
      <div className="w-full max-w-[1920px] 2xl:max-w-full mx-auto px-3 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 text-xs">
        {/* Left: Breadcrumbs Trail */}
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap overflow-hidden">
          {trail.map((item, index) => {
            const isLast = index === trail.length - 1;
            return (
              <React.Fragment key={`${item.label}-${index}`}>
                {index > 0 && (
                  <ChevronRight className="w-3 h-3 text-slate-400 shrink-0 select-none" />
                )}
                {index === 0 ? (
                  <Link 
                    to={item.to || '/'} 
                    className="text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 font-medium"
                    title="Acasă"
                  >
                    <Home className="w-3.5 h-3.5 text-slate-400" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </Link>
                ) : isLast ? (
                  <span className="font-semibold text-slate-900 truncate max-w-[200px] sm:max-w-[340px] md:max-w-[500px]">
                    {item.label}
                  </span>
                ) : item.to ? (
                  <Link 
                    to={item.to} 
                    className="text-slate-500 hover:text-slate-900 transition-colors font-medium truncate max-w-[160px] sm:max-w-[260px]"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-slate-500 font-medium">{item.label}</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Right: Quick Contextual Return / Back Button */}
        {backAction && (
          <div className="shrink-0 flex items-center">
            <Link
              to={backAction.to}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] sm:text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 hover:text-black border border-slate-200 transition-colors shadow-2xs"
            >
              <ArrowLeft className="w-3.5 h-3.5 text-[#E2231A]" />
              <span>Înapoi la {backAction.label}</span>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
